/**
 * Smart Shikka AI Core — PR101
 * Smart Print Local Write + Backup Gate Registry — Approval Required
 */
function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
const BLOCKED_ACTIONS = [
    "auto_delete",
    "child_process",
    "export_file",
    "hidden_drive_scan",
    "import_file",
    "old_assistant_alias",
    "restore_execute_without_approval",
    "secret_literal",
    "shell_command",
    "unapproved_db_write",
    "unapproved_file_write",
    "unapproved_network_call",
    "unknown_provider_call",
    "unknown_tool_execution",
    "unredacted_secret",
    "unsafe_env_dump",
    "broad_runtime_unlock",
    "unscoped_folder_access",
    "write_without_backup",
    "write_without_dry_run",
    "write_without_approval",
    "multi_file_write",
    "delete_without_approval",
    "runtime_unlock",
    "network_call",
    "provider_call",
    "role_mutation",
    "permission_mutation",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
    requiresUserSelectedFolder: true,
    requiresDryRunPreview: true,
    requiresBackupBeforeWrite: true,
    writeWithoutBackupAllowedInPr101: false,
    writeWithoutDryRunAllowedInPr101: false,
    writeWithoutApprovalAllowedInPr101: false,
    multiFileWriteAllowedInPr101: false,
    deleteAllowedInPr101: false,
    importAllowedInPr101: false,
    exportAllowedInPr101: false,
    hiddenScanAllowedInPr101: false,
    secretLiteralAllowedInPr101: false,
    secretReadAllowedInPr101: false,
    shellCommandAllowedInPr101: false,
    childProcessAllowedInPr101: false,
    networkCallAllowedInPr101: false,
    providerCallAllowedInPr101: false,
    dbWriteAllowedInPr101: false,
    unscopedFolderAccessAllowedInPr101: false,
    unapprovedAccessAllowedInPr101: false,
    runtimeUnlockAllowedInPr101: false,
    broadRuntimeUnlockAllowedInPr101: false,
    roleMutationAllowedInPr101: false,
    permissionMutationAllowedInPr101: false,
    restoreWithoutApprovalAllowedInPr101: false,
};
const registry = [
    {
        id: "smart_print_write_backup_gate_summary",
        displayName: "Smart Print Write Backup Gate Summary",
        description: "Summary of the Smart Print local write/backup gate. All writes require user-selected folder, dual approval, dry-run preview, and automatic backup-before-write.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_write_backup_gate_request"],
        relatedUiPreviewIds: ["smart_print_write_backup_gate_route_card"],
        relatedChatPreviewIds: ["smart_print_write_backup_gate_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_write_backup_gate_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_write_backup_gate_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Write requires backup-before-write", "Write requires dry-run preview", "Dual approval required (owner + user)", "User-selected folder required"],
        futurePrScope: ["Full runtime write with streaming audit", "Batch write gate"],
        notes: ["All writes gated behind backup + dry-run + dual approval", "No ungated writes allowed"],
    },
    {
        id: "smart_print_write_dry_run_preview",
        displayName: "Smart Print Write Dry Run Preview",
        description: "Dry-run preview contract for Smart Print writes. Every write must be previewed before execution — no direct writes without dry-run.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_write_dry_run_request"],
        relatedUiPreviewIds: ["smart_print_write_dry_run_route_card"],
        relatedChatPreviewIds: ["smart_print_write_dry_run_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_write_dry_run_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_write_dry_run_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No write without dry-run preview", "Dry-run must show exact changes before execution", "User must approve dry-run output"],
        futurePrScope: ["Interactive dry-run diff viewer", "Dry-run history retention"],
        notes: ["Dry-run is mandatory before any write", "Shows exact file changes before write", "No bypass of dry-run step"],
    },
    {
        id: "smart_print_backup_before_write_required",
        displayName: "Smart Print Backup Before Write Required",
        description: "Automatic backup-before-write contract. A backup of the target file must be created before any write operation proceeds.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_backup_before_write_request"],
        relatedUiPreviewIds: ["smart_print_backup_before_write_route_card"],
        relatedChatPreviewIds: ["smart_print_backup_before_write_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_backup_before_write_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_backup_before_write_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No write without automatic backup", "Backup must complete before write begins", "Backup verification required"],
        futurePrScope: ["Backup retention policy", "Encrypted backup storage"],
        notes: ["Backup is automatic and mandatory before write", "Backup must be verified before write proceeds", "No write without confirmed backup"],
    },
    {
        id: "smart_print_owner_user_approval_required",
        displayName: "Smart Print Owner User Approval Required",
        description: "Dual approval gate for Smart Print writes. Both owner and user must explicitly approve before any write or backup operation.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["smart_print_write_approval_request"],
        relatedUiPreviewIds: ["smart_print_write_approval_route_card"],
        relatedChatPreviewIds: ["smart_print_write_approval_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_write_approval_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_write_approval_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner approval missing", "User approval missing", "Both approvals required before write"],
        futurePrScope: ["Delegated approval flow", "Approval expiry and re-approval"],
        notes: ["Both owner and user must approve", "No bypass of approval gate", "Approval state is immutable once granted"],
    },
    {
        id: "smart_print_single_file_write_scope",
        displayName: "Smart Print Single File Write Scope",
        description: "Scope boundary restricting writes to a single file at a time within the user-selected folder. No multi-file or batch writes allowed.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_single_file_write_request"],
        relatedUiPreviewIds: ["smart_print_single_file_write_route_card"],
        relatedChatPreviewIds: ["smart_print_single_file_write_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_single_file_write_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_single_file_write_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Only single-file writes allowed", "Multi-file or batch writes blocked", "File must be within user-selected folder scope"],
        futurePrScope: ["Controlled batch write with per-file approval", "Multi-file dry-run preview"],
        notes: ["One file at a time only", "No batch or multi-file writes", "Scoped to user-selected folder"],
    },
    {
        id: "smart_print_rollback_metadata_contract",
        displayName: "Smart Print Rollback Metadata Contract",
        description: "Rollback metadata contract ensuring every write operation stores sufficient metadata for future rollback. No write without rollback metadata.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_rollback_metadata_request"],
        relatedUiPreviewIds: ["smart_print_rollback_metadata_route_card"],
        relatedChatPreviewIds: ["smart_print_rollback_metadata_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_rollback_metadata_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_rollback_metadata_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Rollback metadata must be stored before write", "No write without rollback capability", "Rollback path must be verified"],
        futurePrScope: ["Automated rollback execution with approval", "Rollback audit trail"],
        notes: ["Every write stores rollback metadata", "Metadata includes original content hash, timestamp, and path", "Rollback requires separate approval"],
    },
    {
        id: "smart_print_write_audit_evidence",
        displayName: "Smart Print Write Audit Evidence",
        description: "Audit evidence contract for Smart Print writes. Every write operation produces immutable, redacted audit evidence with backup reference.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_write_audit_request"],
        relatedUiPreviewIds: ["smart_print_write_audit_route_card"],
        relatedChatPreviewIds: ["smart_print_write_audit_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_write_audit_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_write_audit_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit evidence incomplete", "No write without audit trail", "Sensitive paths must be redacted in audit"],
        futurePrScope: ["Audit export with approval", "Audit retention and archival policy"],
        notes: ["Every write produces audit evidence", "Audit is append-only and immutable", "Includes backup reference and rollback metadata"],
    },
    {
        id: "smart_print_import_export_still_blocked",
        displayName: "Smart Print Import Export Still Blocked",
        description: "Hard boundary confirming import and export operations remain blocked in PR101. Only single-file write with backup is allowed.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_import_export_blocked_request"],
        relatedUiPreviewIds: ["smart_print_import_export_blocked_route_card"],
        relatedChatPreviewIds: ["smart_print_import_export_blocked_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_import_export_blocked_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_import_export_blocked_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Import operations remain blocked", "Export operations remain blocked", "Only single-file write allowed in PR101"],
        futurePrScope: ["Controlled import with approval gate", "Controlled export with redaction"],
        notes: ["Import and export explicitly blocked", "Deferred to a future gated PR", "No bypass available"],
    },
    {
        id: "smart_print_delete_still_blocked",
        displayName: "Smart Print Delete Still Blocked",
        description: "Hard boundary confirming delete operations remain blocked in PR101. Delete requires a separate future gated PR with its own approval flow.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_delete_blocked_request"],
        relatedUiPreviewIds: ["smart_print_delete_blocked_route_card"],
        relatedChatPreviewIds: ["smart_print_delete_blocked_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_delete_blocked_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_delete_blocked_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Delete operations remain blocked in PR101", "Delete requires separate future PR", "No delete bypass available"],
        futurePrScope: ["Controlled delete with backup-first gate", "Delete audit with rollback"],
        notes: ["Delete explicitly blocked", "Requires separate gated PR", "No silent or automatic deletion"],
    },
    {
        id: "future_smart_print_full_runtime_ready",
        displayName: "Future Smart Print Full Runtime Ready",
        description: "Placeholder readiness marker for future full Smart Print runtime. Not active in PR101 — full runtime requires additional gates beyond write/backup.",
        sourceModules: ["smartShikkaAssistantSmartPrintWriteBackupGateRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        relatedRouteIds: ["smart_print_full_runtime_blocked_request"],
        relatedUiPreviewIds: ["smart_print_full_runtime_blocked_route_card"],
        relatedChatPreviewIds: ["smart_print_full_runtime_blocked_result_card"],
        relatedConversationStatePreviewIds: ["smart_print_full_runtime_blocked_state"],
        relatedApprovalStatePreviewIds: ["smart_print_write_backup_dual_approval_state"],
        relatedActionRequestPreviewIds: ["smart_print_full_runtime_blocked_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Full Smart Print runtime not yet available", "Requires future gated PR series", "Current scope is write/backup gate only"],
        futurePrScope: ["Full Smart Print runtime with streaming", "Import/export/delete gates", "Multi-file operations"],
        notes: ["Future readiness placeholder", "No full runtime in PR101", "Activation requires future PRs"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantSmartPrintWriteBackupGateEntries() {
    return registry;
}
export function getSmartShikkaAssistantSmartPrintWriteBackupGateEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantSmartPrintWriteBackupGateRegistry.js.map